import {Component, Input, OnInit} from '@angular/core';
import {BaseModalComponent} from '../base-modal.component';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastService} from '../../_services/toast.service';
import {LoadingService} from '../../_services/loading.service';
import {CurrencyPipe} from '@angular/common';
import {ModalController} from '@ionic/angular';
import {PaymentCharge} from '../../_models/payment-charge';
import {PaymentChargeRefundService} from '../../_services/payment-charge-refund.service';
import {PaymentChargeRefund} from '../../_models/payment-charge-refund';
import {addCurrency, multiplyCurrency, subtractCurrency} from '../../_utils/currency-math';
import {OrganizationTerminalService} from '../../_services/organization-terminal.service';
import {OrganizationTerminal} from '../../_models/organization-terminal';
import {currency} from '../../_validators/currency.validator';
import {formatCurrencyToNumeric} from '../../_utils/payment-utils';
import {convertPercentage} from '../../_utils/tax-utils';

@Component({
    selector: 'bb-payment-charge-refund',
    templateUrl: './payment-charge-refund.component.html',
    styleUrls: ['./payment-charge-refund.component.scss']
})
export class PaymentChargeRefundComponent extends BaseModalComponent implements OnInit {
    @Input() paymentCharge: PaymentCharge;
    formGroup: FormGroup;
    available: number;
    availableTip: number;
    fullAvailable: number;
    manual = false;
    terminal: OrganizationTerminal;
    type: string;

    constructor(
        private formBuilder: FormBuilder,
        private toastService: ToastService,
        private loadingService: LoadingService,
        private currencyPipe: CurrencyPipe,
        private paymentChargeRefundService: PaymentChargeRefundService,
        private organizationTerminalService: OrganizationTerminalService,
        modalController: ModalController
    ) {
        super(modalController);
    }

    ngOnInit(): void {
        this.subscribe(this.organizationTerminalService.current.subscribe(r => this.terminal = r));
        this.available = subtractCurrency(subtractCurrency(this.paymentCharge.principal, this.paymentCharge.totalPrincipalRefunds()),
            this.paymentCharge.refundedSurcharge);
        this.availableTip = subtractCurrency(this.paymentCharge.tip, this.paymentCharge.totalTipRefunds());
        this.fullAvailable = addCurrency(this.available, this.availableTip);

        this.formGroup = this.formBuilder.group({
            principal: [
                '',
                [currency(false, 0, this.available)]
            ],
            tip: [
                '',
                [currency(false, 0, this.availableTip)]
            ],
            comment: ['', Validators.maxLength(2000)],
            parentId: this.paymentCharge.id,
            creditTab: '',
            creditType: ''
        });
    }

    creditChanged() {
        if (this.formGroup.get('creditTab').value === 'REFUND_AND_COMP') {
            if (!this.formGroup.get('creditType').value) {
                this.formGroup.get('creditType').setValue('REWARD');
            }
            this.formGroup.get('creditType').setValidators([Validators.required]);
        } else {
            this.formGroup.get('creditType').setValidators([]);
        }
        this.formGroup.controls.creditType.updateValueAndValidity();
    }

    async onSubmit() {
        this.loadingService.present(!!this.paymentCharge.registerId ? `Waiting on terminal.` : null);
        const paymentChargeRefund = new PaymentChargeRefund(this.formGroup.value);

        if (typeof paymentChargeRefund.tip === 'string') {
            paymentChargeRefund.tip = formatCurrencyToNumeric(this.formGroup.controls.tip.value);
        }
        if (typeof paymentChargeRefund.principal === 'string') {
            paymentChargeRefund.principal = formatCurrencyToNumeric(this.formGroup.controls.principal.value);
        }
        paymentChargeRefund.paymentCharge = this.paymentCharge;
        paymentChargeRefund.creditTab = this.formGroup.controls.creditTab.value === 'REFUND_AND_COMP';
        paymentChargeRefund.total = addCurrency(paymentChargeRefund.principal, paymentChargeRefund.tip);

        if (!!this.paymentCharge.surcharge && !!this.paymentCharge.surchargePercent) {
            const balance = subtractCurrency(addCurrency(this.available, this.availableTip), paymentChargeRefund.total);

            if (balance > 0) {
                const newSurchargeAgainst = this.paymentCharge.surchargeIncludeTip ?
                    addCurrency(paymentChargeRefund.principal, paymentChargeRefund.tip) :
                    paymentChargeRefund;

                paymentChargeRefund.surcharge = multiplyCurrency(newSurchargeAgainst,
                    convertPercentage(this.paymentCharge.surchargePercent));
            } else {
                paymentChargeRefund.surcharge = subtractCurrency(this.paymentCharge.surcharge, this.paymentCharge.refundedSurcharge);
                paymentChargeRefund.principal = subtractCurrency(paymentChargeRefund.principal, paymentChargeRefund.surcharge);
            }
        }

        paymentChargeRefund.total = addCurrency(
            addCurrency(paymentChargeRefund.principal, paymentChargeRefund.tip), paymentChargeRefund.surcharge);

        if (!paymentChargeRefund.creditTab) {
            paymentChargeRefund.creditType = null;
        }

        if (!!this.terminal) {
            paymentChargeRefund.terminalId = this.terminal.terminalId;
        }

        this.paymentChargeRefundService.refund(paymentChargeRefund).subscribe(async (refund: PaymentChargeRefund) => {
            let message;
            if (!!refund) {
                if (refund.pending) {
                    message = `A refund of ${this.currencyPipe.transform(refund.total)} is pending. It will be applied in the next 2 business days.`;
                    if (refund.creditTab) {
                        message += ` The tab has been credited ${this.currencyPipe.transform(refund.total)}.`;
                    }
                } else {
                    message = `A refund of ${this.currencyPipe.transform(refund.total)} has been applied${refund.creditTab
                        ? ' and credited'
                        : ''}.`;
                }
            } else {
                message = 'The transaction has been fully refunded and voided.';
            }

            await this.toastService.success(message);

            await this.close(true);
            this.loadingService.dismiss();
        }, (error) => {
            this.paymentChargeRefundService.errorHandler(error);
        });
    }

    maxRefund() {
        this.formGroup.controls.principal.setValue(this.available);
    }

    maxTip() {
        this.formGroup.controls.tip.setValue(this.availableTip);
    }

    setManual(m) {
        this.manual = m;
    }

    refund(type) {
        this.type = type;
        if (type === 'FULL') {
            this.formGroup.controls.principal.setValue(this.available);
            this.formGroup.controls.tip.setValue(this.availableTip);
            this.formGroup.controls.creditTab.setValue('REFUND');
            this.onSubmit();
        } else if (type === 'FULL_COMP') {
            this.formGroup.controls.principal.setValue(this.available);
            this.formGroup.controls.tip.setValue(this.availableTip);
            this.formGroup.controls.creditType.setValue('OTHER');
            this.formGroup.controls.creditTab.setValue('REFUND_AND_COMP');
            this.onSubmit();
        } else if (type === 'TIP') {
            this.formGroup.controls.principal.setValue(0);
            this.formGroup.controls.tip.setValue(this.availableTip);
            this.formGroup.controls.creditTab.setValue('REFUND');
            this.onSubmit();
        }
    }
}
